/**
 * DirectionProvider — sets dir/ltr context for all children.
 * All feature components consume via useDirection() hook.
 * @layer-1 — shared component, imported by AppShell and feature wrappers.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

interface DirectionContextValue {
  dir: 'rtl' | 'ltr';
  isRTL: boolean;
  language: 'ar' | 'en';
}

const DirectionContext = createContext<DirectionContextValue>({
  dir: 'rtl',
  isRTL: true,
  language: 'ar',
});

interface DirectionProviderProps {
  dir: 'rtl' | 'ltr';
  children: ReactNode;
}

/**
 * DirectionProvider wraps sections that need specific direction context.
 * Sets dir attribute on container + exposes isRTL/language via context.
 */
export function DirectionProvider({ dir, children }: DirectionProviderProps) {
  const value = useMemo<DirectionContextValue>(() => ({
    dir,
    isRTL: dir === 'rtl',
    language: dir === 'rtl' ? 'ar' as const : 'en' as const,
  }), [dir]);

  return (
    <DirectionContext.Provider value={value}>
      <div dir={dir} lang={value.language}>
        {children}
      </div>
    </DirectionContext.Provider>
  );
}

/**
 * useDirection — consume current direction context.
 * Use instead of reading document.documentElement.dir directly.
 */
export function useDirection(): DirectionContextValue {
  return useContext(DirectionContext);
}
